import { useEffect, useState, useCallback } from "react";
import api, { mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import { Switch } from "../components/ui/switch";
import { APP_VERSION } from "../version";
import {
  Monitor, Plus, Zap, ListVideo, Type, Trash2, Wifi, WifiOff, RefreshCw,
  Settings2, Volume2, VolumeX, Maximize, Minimize, MonitorPlay,
  CheckCircle2, AlertTriangle, HelpCircle,
} from "lucide-react";

function Stat({ label, value, accent }) {
  return (
    <div className="bg-card border border-border rounded-md p-4">
      <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{label}</p>
      <p className={`text-3xl font-display font-bold mt-1 ${accent || ""}`}>{value}</p>
    </div>
  );
}

function VersionBadge({ version, online }) {
  if (!version) {
    return (
      <span data-testid="tv-version-unknown" className="inline-flex items-center gap-1 mt-1.5 text-[10px] text-muted-foreground/70">
        <HelpCircle className="w-3 h-3" /> Build unknown — reboot to update
      </span>
    );
  }
  const latest = version === APP_VERSION;
  return latest ? (
    <span data-testid="tv-version-latest" className="inline-flex items-center gap-1 mt-1.5 text-[10px] text-green-500">
      <CheckCircle2 className="w-3 h-3" /> Up to date · v{version}
    </span>
  ) : (
    <span data-testid="tv-version-stale" className="inline-flex items-center gap-1 mt-1.5 text-[10px] text-amber-500" title={`Running v${version}, latest is v${APP_VERSION}`}>
      <AlertTriangle className="w-3 h-3" /> {online ? "Reboot to update" : "Update pending"} · v{version}
    </span>
  );
}


function TVTile({ tv, playlists, media, onChange, isAdmin }) {
  const [dialog, setDialog] = useState(null);
  const [ticker, setTicker] = useState(tv.ticker || "");
  const [tickerSize, setTickerSize] = useState(tv.ticker_size || "medium");
  const [pushType, setPushType] = useState("media");
  const [selMedia, setSelMedia] = useState("");
  const [selPlaylist, setSelPlaylist] = useState("");
  const [duration, setDuration] = useState(30);
  const [keepOn, setKeepOn] = useState(true);
  const [volume, setVolume] = useState(tv.volume ?? 80);
  const [muted, setMuted] = useState(tv.muted ?? false);
  const [fitMode, setFitMode] = useState(tv.fit_mode || "cover");
  const [interstitial, setInterstitial] = useState(tv.interstitial_media_id || "none");
  const [interEvery, setInterEvery] = useState(tv.interstitial_every || 1);
  const assigned = playlists.find((p) => p.id === tv.playlist_id);
  const pushActive = (() => {
    const p = tv.instant_push;
    if (!p) return false;
    if (p.permanent) return true;
    try { return new Date(p.expires_at) > new Date(); } catch (e) { return false; }
  })();
  const pushPlaylist = pushActive && tv.instant_push?.playlist_id
    ? playlists.find((p) => p.id === tv.instant_push.playlist_id) : null;
  const activePlaylist = pushPlaylist || assigned;

  const assignPlaylist = async (pid) => {
    await api.post(`/tvs/${tv.id}/assign`, { playlist_id: pid === "none" ? null : pid });
    toast.success("Playlist assigned");
    onChange();
  };

  const push = async () => {
    const dur = keepOn ? 0 : Number(duration);
    if (pushType === "playlist") {
      if (!selPlaylist) return toast.error("Pick a playlist to push");
      await api.post(`/tvs/${tv.id}/push`, { playlist_id: selPlaylist, duration: dur });
    } else {
      if (!selMedia) return toast.error("Pick media to push");
      await api.post(`/tvs/${tv.id}/push`, { media_id: selMedia, duration: dur });
    }
    toast.success(keepOn ? `Now showing on ${tv.name}` : `Flash offer pushed to ${tv.name}`);
    setDialog(null);
    onChange();
  };

  const saveSettings = async () => {
    await api.post(`/tvs/${tv.id}/settings`, {
      volume: Number(volume), muted, fit_mode: fitMode,
      interstitial_media_id: interstitial === "none" ? "" : interstitial,
      interstitial_every: Math.max(Number(interEvery) || 1, 1),
    });
    toast.success("Display settings saved");
    setDialog(null);
    onChange();
  };

  const saveTicker = async () => {
    await api.post(`/tvs/${tv.id}/ticker`, { ticker, size: tickerSize });
    toast.success("Ticker updated");
    setDialog(null);
    onChange();
  };

  const remove = async () => {
    if (!window.confirm(`Remove "${tv.name}"? This unpairs the TV and deletes it from the dashboard. The screen will need to be paired again with a new code.`)) return;
    await api.delete(`/tvs/${tv.id}`);
    toast.success("TV removed");
    onChange();
  };

  const playingItem = activePlaylist?.items?.[0] || (pushActive && tv.instant_push?.media_id ? { media_id: tv.instant_push.media_id } : null);

  return (
    <div data-testid={`tv-tile-${tv.id}`} className="bg-card border border-border rounded-md overflow-hidden hover:border-muted-foreground/40 transition-colors duration-200 fade-up">
      <div className="relative aspect-video bg-black flex items-center justify-center overflow-hidden">
        {playingItem ? (
          <img src={mediaUrl(playingItem.media_id)} alt="" className="w-full h-full object-cover" />
        ) : (
          <Monitor className="w-10 h-10 text-muted-foreground/30" />
        )}
        <div className="absolute top-2 left-2 flex items-center gap-1.5 bg-black/60 backdrop-blur px-2 py-1 rounded-full">
          <span className={`w-2 h-2 rounded-full live-dot ${tv.online ? "bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.7)]" : "bg-red-500"}`} />
          <span className="text-[10px] uppercase tracking-wider font-medium">{tv.online ? "Online" : "Offline"}</span>
        </div>
        {tv.ticker && (
          <div className="absolute bottom-0 inset-x-0 bg-primary/80 text-white text-[10px] py-0.5 overflow-hidden">
            <span className="ticker-track px-2">{tv.ticker}</span>
          </div>
        )}
      </div>
      <div className="p-3">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <h3 className="font-display font-semibold truncate">{tv.name}</h3>
            <p className="text-xs text-muted-foreground truncate">{tv.zone || "No zone"}</p>
            <VersionBadge version={tv.player_version} online={tv.online} />
          </div>
          {isAdmin && (
            <button data-testid={`tv-delete-${tv.id}`} onClick={remove} title="Remove this TV" className="p-1.5 text-muted-foreground hover:text-destructive transition-colors">
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-2 truncate">
          {activePlaylist
            ? <span className="text-foreground">▶ {activePlaylist.name}{pushPlaylist ? " · pushed" : ""}</span>
            : (playingItem ? <span className="text-foreground">▶ Pushed content</span> : "No playlist")}
        </p>

        <div className="mt-3">
          <Select value={tv.playlist_id || "none"} onValueChange={assignPlaylist}>
            <SelectTrigger data-testid={`tv-playlist-select-${tv.id}`} className="h-9 text-xs">
              <SelectValue placeholder="Assign playlist" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No playlist</SelectItem>
              {playlists.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-3 gap-2 mt-2">
          <Dialog open={dialog === "push"} onOpenChange={(o) => setDialog(o ? "push" : null)}>
            <DialogTrigger asChild>
              <Button data-testid={`tv-push-${tv.id}`} variant="secondary" size="sm" className="text-xs px-1.5">
                <Zap className="w-3.5 h-3.5 mr-1" /> Push
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Instant Push — {tv.name}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <Button type="button" size="sm" variant={pushType === "media" ? "default" : "secondary"}
                    onClick={() => setPushType("media")} data-testid="push-type-media">Single Media</Button>
                  <Button type="button" size="sm" variant={pushType === "playlist" ? "default" : "secondary"}
                    onClick={() => setPushType("playlist")} data-testid="push-type-playlist">Playlist</Button>
                </div>
                {pushType === "media" ? (
                  <>
                    <Label>Media</Label>
                    <Select value={selMedia} onValueChange={setSelMedia}>
                      <SelectTrigger data-testid="push-media-select"><SelectValue placeholder="Select media" /></SelectTrigger>
                      <SelectContent>
                        {media.map((m) => <SelectItem key={m.id} value={m.id}>{m.original_filename}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </>
                ) : (
                  <>
                    <Label>Playlist</Label>
                    <Select value={selPlaylist} onValueChange={setSelPlaylist}>
                      <SelectTrigger data-testid="push-playlist-select"><SelectValue placeholder="Select playlist" /></SelectTrigger>
                      <SelectContent>
                        {playlists.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.items?.length || 0})</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </>
                )}
                <div className="flex items-center justify-between pt-1">
                  <Label className="flex-1">Keep on screen until I change it</Label>
                  <Switch checked={keepOn} onCheckedChange={setKeepOn} data-testid="push-keep-on" />
                </div>
                {!keepOn && (
                  <>
                    <Label>Show for (seconds), then return to playlist</Label>
                    <Input type="number" value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="push-duration" />
                  </>
                )}
              </div>
              <DialogFooter>
                <Button onClick={push} data-testid="push-confirm"><Zap className="w-4 h-4 mr-1" /> Push Now</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={dialog === "display"} onOpenChange={(o) => { setDialog(o ? "display" : null); setVolume(tv.volume ?? 80); setMuted(tv.muted ?? false); setFitMode(tv.fit_mode || "cover"); setInterstitial(tv.interstitial_media_id || "none"); setInterEvery(tv.interstitial_every || 1); }}>
            <DialogTrigger asChild>
              <Button data-testid={`tv-display-${tv.id}`} variant="secondary" size="sm" className="text-xs px-1.5">
                <Settings2 className="w-3.5 h-3.5 mr-1" /> Display
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Display & Sound — {tv.name}</DialogTitle></DialogHeader>
              <div className="space-y-5">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">{muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />} Mute audio</Label>
                  <Switch checked={muted} onCheckedChange={setMuted} data-testid="display-mute-switch" />
                </div>
                <div className={muted ? "opacity-40 pointer-events-none" : ""}>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Volume</Label>
                    <span className="text-sm text-muted-foreground">{volume}%</span>
                  </div>
                  <Slider value={[volume]} onValueChange={(v) => setVolume(v[0])} max={100} step={5} data-testid="display-volume-slider" />
                </div>
                <div>
                  <Label className="mb-2 block">Screen fit (for TVs where the browser bar crops the edges)</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => setFitMode("cover")} data-testid="display-fit-cover"
                      className={`flex flex-col items-center gap-1 rounded-md border p-3 text-xs transition-colors ${fitMode === "cover" ? "border-primary ring-1 ring-primary" : "border-border hover:border-muted-foreground"}`}>
                      <Maximize className="w-5 h-5" /> Fill (crops edges)
                    </button>
                    <button type="button" onClick={() => setFitMode("contain")} data-testid="display-fit-contain"
                      className={`flex flex-col items-center gap-1 rounded-md border p-3 text-xs transition-colors ${fitMode === "contain" ? "border-primary ring-1 ring-primary" : "border-border hover:border-muted-foreground"}`}>
                      <Minimize className="w-5 h-5" /> Fit (shows all)
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="mb-1 block">Interstitial Video</Label>
                  <p className="text-[11px] text-muted-foreground/80 mb-2">A recurring clip that plays between every item in this TV's loop — use it for branding, a logo sting, a promo, or any repeated message.</p>
                  <Select value={interstitial} onValueChange={setInterstitial}>
                    <SelectTrigger data-testid="display-interstitial-select"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {media.map((m) => <SelectItem key={m.id} value={m.id}>{m.original_filename}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {interstitial !== "none" && (
                    <div className="flex items-center gap-2 mt-3">
                      <Label className="text-xs whitespace-nowrap">Play it after every</Label>
                      <Input type="number" min="1" value={interEvery} onChange={(e) => setInterEvery(e.target.value)} className="w-16 h-8" data-testid="display-interstitial-every" />
                      <span className="text-xs text-muted-foreground">item(s) in the loop</span>
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button onClick={saveSettings} data-testid="display-save">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={dialog === "ticker"} onOpenChange={(o) => { setDialog(o ? "ticker" : null); setTicker(tv.ticker || ""); setTickerSize(tv.ticker_size || "medium"); }}>
            <DialogTrigger asChild>
              <Button data-testid={`tv-ticker-${tv.id}`} variant="secondary" size="sm" className="text-xs px-1.5">
                <Type className="w-3.5 h-3.5 mr-1" /> Ticker
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>News Ticker — {tv.name}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Ticker message</Label>
                  <Input value={ticker} onChange={(e) => setTicker(e.target.value)} placeholder="e.g. 50% off all dairy until 6 PM!" data-testid="ticker-input" className="mt-1.5" />
                </div>
                <div>
                  <Label>Text size</Label>
                  <Select value={tickerSize} onValueChange={setTickerSize}>
                    <SelectTrigger data-testid="ticker-size-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="small">Small</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="large">Large (most eye-catching)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* live preview */}
                <div>
                  <Label className="text-muted-foreground">Preview</Label>
                  <div className="mt-1.5 rounded-md overflow-hidden relative h-14 flex items-center"
                    style={{ background: "linear-gradient(90deg,#05205c,#0A84FF 45%,#19E3FF)", boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.15)" }}>
                    <div className="absolute left-0 top-0 bottom-0 flex items-center gap-1.5 px-3 bg-black/40 text-white font-black tracking-widest text-[10px] z-10">
                      <span className="w-2 h-2 rounded-full bg-red-500 live-dot" /> LIVE
                    </div>
                    <span className="ticker-track inline-block whitespace-nowrap font-extrabold text-white pl-[55%]"
                      style={{ fontSize: tickerSize === "large" ? "26px" : tickerSize === "small" ? "14px" : "20px" }}>
                      {ticker || "Your live offer text will scroll here…"}
                    </span>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="secondary" onClick={() => { setTicker(""); }}>Clear text</Button>
                <Button onClick={saveTicker} data-testid="ticker-save">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}

export default function Overview() {
  const { user } = useAuth();
  const [tvs, setTvs] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [media, setMedia] = useState([]);
  const [stats, setStats] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [zone, setZone] = useState("");

  // Refresh from a single response without ever blanking good data on a transient blip:
  // a 200 that comes back empty while we already have items is treated as a hiccup, not a wipe.
  const applyTvs = useCallback((d) => {
    if (!Array.isArray(d)) return;
    setTvs((prev) => (d.length === 0 && prev.length > 0 ? prev : d));
  }, []);

  const load = useCallback(async () => {
    const [t, p, m, s] = await Promise.allSettled([
      api.get("/tvs"), api.get("/playlists"), api.get("/media"), api.get("/stats"),
    ]);
    if (t.status === "fulfilled") applyTvs(t.value.data);
    if (p.status === "fulfilled" && Array.isArray(p.value.data)) setPlaylists(p.value.data);
    if (m.status === "fulfilled" && Array.isArray(m.value.data)) {
      setMedia((prev) => (m.value.data.length === 0 && prev.length > 0 ? prev : m.value.data));
    }
    if (s.status === "fulfilled" && s.value.data && Object.keys(s.value.data).length) setStats(s.value.data);
    setLoaded(true);
  }, [applyTvs]);

  useEffect(() => {
    load();
    const i = setInterval(() => {
      api.get("/tvs").then((r) => applyTvs(r.data)).catch(() => {});
      api.get("/stats").then((r) => { if (r.data && Object.keys(r.data).length) setStats(r.data); }).catch(() => {});
    }, 8000);
    const onWake = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);
    return () => {
      clearInterval(i);
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
    };
  }, [load, applyTvs]);

  const addTV = async () => {
    if (!code || !name) return toast.error("Enter pairing code and name");
    try {
      await api.post("/tvs/pair", { code, name, zone });
      toast.success(`${name} paired!`);
      setAddOpen(false); setCode(""); setName(""); setZone("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Pairing failed");
    }
  };

  const refreshAllTvs = async () => {
    try {
      await api.post("/tvs/refresh");
      toast.success("All TVs will reload within ~5 seconds (new content & updates)");
    } catch { toast.error("Could not refresh TVs"); }
  };

  return (
    <div className="p-4 md:p-8">
      <div className="flex items-center justify-between gap-2 mb-5">
        <div className="min-w-0">
          <h1 className="text-2xl md:text-3xl font-display font-bold tracking-tight">TV Wall</h1>
          <p className="text-sm text-muted-foreground mt-0.5 truncate">Live status of every screen in your store</p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <Button variant="secondary" size="icon" onClick={load} data-testid="refresh-button"><RefreshCw className="w-4 h-4" /></Button>
          <Button variant="secondary" size="sm" onClick={refreshAllTvs} data-testid="refresh-all-tvs-button" className="text-xs">
            <MonitorPlay className="w-4 h-4 mr-1" /> Refresh TVs
          </Button>
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-tv-button"><Plus className="w-4 h-4 mr-1" /> Add TV</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Pair a new TV</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">Power on the TV and open the Player. Enter the 6-digit code it shows.</p>
                <div>
                  <Label>Pairing Code</Label>
                  <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="000000" maxLength={6} className="font-mono text-lg tracking-[0.3em]" data-testid="pair-code-input" />
                </div>
                <div>
                  <Label>TV Name</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Dairy Section TV" data-testid="pair-name-input" />
                </div>
                <div>
                  <Label>Zone</Label>
                  <Input value={zone} onChange={(e) => setZone(e.target.value)} placeholder="Dairy" data-testid="pair-zone-input" />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={addTV} data-testid="pair-confirm">Pair TV</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat label="Total TVs" value={stats.total_tvs ?? 0} />
        <Stat label="Online" value={stats.online_tvs ?? 0} accent="text-green-500" />
        <Stat label="Offline" value={stats.offline_tvs ?? 0} accent="text-red-500" />
        <Stat label="Media Files" value={stats.media_count ?? 0} accent="text-primary" />
      </div>

      {!loaded && tvs.length === 0 ? (
        <div className="border border-dashed border-border rounded-md py-20 text-center" data-testid="tv-wall-loading">
          <RefreshCw className="w-8 h-8 mx-auto text-muted-foreground/30 mb-4 animate-spin" />
          <p className="text-muted-foreground">Loading your screens…</p>
        </div>
      ) : tvs.length === 0 ? (
        <div className="border border-dashed border-border rounded-md py-20 text-center">
          <Monitor className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
          <p className="text-muted-foreground">No TVs paired yet.</p>
          <p className="text-sm text-muted-foreground/70 mt-1">Open <span className="font-mono text-primary">/player</span> on a screen, then click "Add TV".</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {tvs.map((tv) => (
            <TVTile key={tv.id} tv={tv} playlists={playlists} media={media} onChange={load} isAdmin={user?.role === "admin"} />
          ))}
        </div>
      )}
    </div>
  );
}
